import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useDocuments } from '../lib/queries';
import { fmtDate, fmtMoney, fmtMonth } from '../lib/format';
import type { Unbilled } from '../lib/types';
import { useCan } from '../lib/auth';
import { Button, Callout, Dialog, Empty, ErrorState, Field, Input, Ltr, Select, SkeletonRows, useToast } from '../components/ui';
import { useOpenDocument } from '../components/Layout';

type Group = Unbilled['customers'][number];

export function BillingPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? '';
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['billing', month],
    queryFn: () => api<Unbilled>(`/api/billing/delivery-notes${qs({ month })}`),
  });
  const [linking, setLinking] = useState<Group | null>(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>חיוב סוף חודש</h1>
          <div className="sub">תעודות משלוח שעדיין לא נכללו בחשבונית מרכזת, לפי לקוח. "הפקת חשבונית מרכזת" פותחת טיוטה עם שורה לכל תעודה — בודקים בתצוגה המקדימה ומפיקים; התעודות מסומנות כמחויבות ומקושרות לחשבונית גם במורנינג.</div>
        </div>
        <label className="row small muted">
          תעודות עד סוף
          <Input type="month" value={data?.month ?? month} onChange={(e) => setParams(e.target.value ? { month: e.target.value } : {}, { replace: true })} style={{ width: 170 }} />
        </label>
      </div>

      {linking ? <LinkDialog group={linking} onClose={() => setLinking(null)} /> : null}

      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : (
        <div className="stack">
          <section className="kpis">
            <div className="kpi">
              <div className="kpi-label">תעודות ממתינות לחיוב</div>
              <div className="kpi-value">{data?.totals.notes ?? '—'}</div>
              <div className="kpi-meta">{data ? `עד ${fmtDate(data.until)}` : ''}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">לקוחות</div>
              <div className="kpi-value">{data?.totals.customers ?? '—'}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">סכום לחיוב (לפני מע״מ)</div>
              <div className="kpi-value">{fmtMoney(data?.totals.subtotal, { round: true })}</div>
              <div className="kpi-meta">כולל מע״מ {fmtMoney(data?.totals.total, { round: true })}</div>
            </div>
          </section>
          {data && data.laterNotes ? <Callout tone="info">עוד {data.laterNotes} תעודות מתאריך מאוחר יותר ממתינות לחודש הבא.</Callout> : null}

          {isLoading ? (
            <div className="panel"><table className="table"><tbody><SkeletonRows rows={5} cols={5} /></tbody></table></div>
          ) : data && data.customers.length === 0 ? (
            <div className="panel"><Empty title="אין תעודות משלוח שממתינות לחיוב" body={`כל תעודות המשלוח עד ${fmtMonth(data.month)} כלולות בחשבוניות.`} /></div>
          ) : (
            data?.customers.map((g) => <GroupCard key={g.customerId} group={g} month={data.month} onLink={() => setLinking(g)} />)
          )}
        </div>
      )}
    </>
  );
}

function GroupCard({ group, month, onLink }: { group: Group; month: string; onLink: () => void }) {
  const open = useOpenDocument();
  const toast = useToast();
  const can = useCan();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const makeInvoice = async () => {
    setBusy(true);
    try {
      const r = await api<{ id: number; reused: boolean }>('/api/billing/invoice', { body: { customerId: group.customerId, deliveryNoteIds: group.notes.map((n) => n.id), month } });
      if (r.reused) toast.success('נפתחה הטיוטה שכבר קיימת לתעודות האלה');
      navigate(`/orders/${r.id}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const numbers = group.notes.map((n) => n.number).filter(Boolean).join(', ');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(numbers);
      toast.success('מספרי התעודות הועתקו — להדבקה בחשבונית במורנינג');
    } catch {
      toast.error(new Error('ההעתקה נכשלה'));
    }
  };
  return (
    <section className="panel">
      <div className="panel-head">
        <Link to={`/customers/${group.customerId}?tab=delivery`} className="strong" style={{ color: 'var(--ink)' }}>{group.businessName}</Link>
        <span className="small muted">{group.count} תעודות · הוותיקה מ-{fmtDate(group.oldest)}</span>
        <span className="spacer" />
        <span className="strong tabular">{fmtMoney(group.subtotal)}</span>
        <span className="small muted">לפני מע״מ</span>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>תעודה</th>
              <th>תאריך</th>
              <th className="hide-sm">סניף / כתובת</th>
              <th className="num">לפני מע״מ</th>
              <th className="num">כולל מע״מ</th>
            </tr>
          </thead>
          <tbody>
            {group.notes.map((n) => (
              <tr key={n.id} className="clickable" onClick={() => open(n.id)}>
                <td><Ltr className="tabular strong">{n.number ?? '—'}</Ltr></td>
                <td className="tabular">{fmtDate(n.issueDate)}</td>
                <td className="hide-sm small">{n.branch ?? n.deliveryAddress ?? <span className="faint">—</span>}</td>
                <td className="num">{fmtMoney(n.subtotal)}</td>
                <td className="num">{fmtMoney(n.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel-foot row wrap">
        <Button size="sm" onClick={copy}>העתקת מספרי התעודות</Button>
        <Button size="sm" onClick={onLink}>כבר הופקה חשבונית — קישור ידני</Button>
        {can('orders.edit') ? (
          <Button size="sm" variant="primary" loading={busy} disabled={group.missingTotals > 0} title={group.missingTotals ? 'יש תעודות בלי סכום — יש לתקן אותן קודם' : undefined} onClick={makeInvoice}>
            הפקת חשבונית מרכזת
          </Button>
        ) : null}
        {group.missingTotals ? <span className="small" style={{ color: 'var(--warn)' }}>{group.missingTotals} תעודות ללא סכום</span> : null}
        <span className="small muted">חודש {fmtMonth(month)}</span>
      </div>
    </section>
  );
}

function LinkDialog({ group, onClose }: { group: Group; onClose: () => void }) {
  const invoices = useDocuments({ customerId: group.customerId, includeBranches: true, types: 'tax_invoice,tax_invoice_receipt', sort: 'issueDate', dir: 'desc', pageSize: 30 });
  const [invoiceId, setInvoiceId] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set(group.notes.map((n) => n.id)));
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ linked: number }>('/api/billing/link', { body: { invoiceId: Number(invoiceId), deliveryNoteIds: [...selected] } });
      for (const k of ['billing', 'documents', 'document', 'customer', 'dashboard', 'workplan']) qc.invalidateQueries({ queryKey: [k] });
      toast.success(`${r.linked} תעודות סומנו כמחויבות`);
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`קישור תעודות משלוח לחשבונית — ${group.businessName}`}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy} disabled={!invoiceId || selected.size === 0}>סימון כמחויבות</Button>
          <Button onClick={onClose}>ביטול</Button>
        </>
      }
    >
      <div className="stack">
        <Field label="החשבונית שבה חויבו">
          <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
            <option value="">בחירת חשבונית…</option>
            {invoices.data?.items.map((d) => (
              <option key={d.id} value={d.id}>
                {d.typeLabel} {d.number} · {fmtDate(d.issueDate)} · {fmtMoney(d.total)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="stack" style={{ gap: 4 }}>
          {group.notes.map((n) => (
            <label key={n.id} className="row small">
              <input
                type="checkbox"
                checked={selected.has(n.id)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(n.id);
                  else next.delete(n.id);
                  setSelected(next);
                }}
              />
              תעודה <Ltr>{n.number}</Ltr> · {fmtDate(n.issueDate)} · {fmtMoney(n.total)}
            </label>
          ))}
        </div>
        <p className="small muted">לשימוש כשהחשבונית במורנינג הופקה בלי לציין את מספרי התעודות. אפשר לבטל את הקישור מתוך תעודת המשלוח.</p>
      </div>
    </Dialog>
  );
}
